import { useMemo } from 'react';

function isSkuSpecLabel(label) {
    return /^sku$/i.test(String(label || '').trim());
}

/** True when Admin specs have real rows beyond SKU (which is always appended). */
export function hasRichSpecifications(product) {
    return (product?.specifications || []).some((row) => {
        const label = String(row?.label || '').trim();
        const value = String(row?.value || '').trim();
        return Boolean(label && value && !isSkuSpecLabel(label));
    });
}

export default function Specifications({ product, selectedVariant }) {
    const specs = useMemo(() => {
        const rows = [];
        const seen = new Set();

        const push = (label, value) => {
            const l = String(label || '').trim();
            const v = String(value || '').trim();
            if (!l || !v) return;
            const key = l.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            rows.push({ label: l, value: v });
        };

        // Freeform storefront specs from Admin
        for (const row of product?.specifications || []) {
            push(row.label, row.value);
        }

        // SKU from selected variant (only auto field — not RAM/Storage/Color)
        if (selectedVariant?.sku) {
            push('SKU', selectedVariant.sku);
        }

        return rows;
    }, [product, selectedVariant]);

    if (specs.length === 0) {
        return (
            <p className="text-sm text-muted-foreground py-2 text-center">
                No specifications available for this product.
            </p>
        );
    }

    return (
        <div className="overflow-hidden rounded-lg">
            <table className="w-full">
                <tbody>
                    {specs.map((spec, idx) => (
                        <tr
                            key={`${spec.label}-${idx}`}
                            className={idx % 2 === 0 ? 'bg-muted/40' : 'bg-background'}
                        >
                            <td className="px-4 py-2.5 text-sm text-muted-foreground w-[38%] align-top font-medium">
                                {spec.label}
                            </td>
                            <td className="px-4 py-2.5 text-sm text-foreground">
                                {spec.value}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
